import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { VersionSummary } from '../../shared/types';

export interface FrameEvent {
  type: 'click' | 'error' | 'loaded';
  data: { url?: string; message?: string; source?: string; line?: number; ms?: number };
}

type MediaVersion = Pick<VersionSummary, 'id' | 'kind' | 'width' | 'height' | 'previewUrl' | 'originalName'>;

export const sizeOf = (v: Pick<VersionSummary, 'width' | 'height'>) => ({
  w: v.width ?? 300,
  h: v.height ?? 250,
});

function MediaElement({
  version,
  replayKey,
  interactive,
  iframeRef,
  videoRef,
  loop,
  controls,
}: {
  version: MediaVersion;
  replayKey: number;
  interactive: boolean;
  iframeRef?: React.Ref<HTMLIFrameElement>;
  videoRef?: React.Ref<HTMLVideoElement>;
  loop?: boolean;
  controls?: boolean;
}) {
  const { w, h } = sizeOf(version);
  const style = { pointerEvents: interactive ? undefined : ('none' as const) };
  if (version.kind === 'html5') {
    return (
      <iframe
        key={replayKey}
        ref={iframeRef}
        src={version.previewUrl}
        width={w}
        height={h}
        title={`Vorschau ${version.originalName}`}
        // Kein allow-same-origin: Banner-Code läuft isoliert und kommt nicht an Cookies oder die App.
        sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox allow-forms"
        scrolling="no"
        style={style}
      />
    );
  }
  if (version.kind === 'video') {
    return (
      <video
        key={replayKey}
        ref={videoRef}
        src={version.previewUrl}
        width={w}
        height={h}
        autoPlay
        muted
        playsInline
        loop={loop}
        controls={controls}
        style={{ ...style, width: w, height: h, objectFit: 'contain', background: '#000' }}
      />
    );
  }
  // GIFs starten nur neu, wenn sich die URL ändert – daher der Zähler im Query-String.
  const src =
    version.kind === 'gif' && replayKey ? `${version.previewUrl}?r=${replayKey}` : version.previewUrl;
  return <img src={src} width={w} height={h} alt={version.originalName} draggable={false} style={style} />;
}

/** Werbemittel in exakter Pixelgröße, optional skaliert, mit Ereignissen aus dem Banner (Klicks, Fehler). */
export function BannerFrame({
  version,
  scale = 1,
  replayKey = 0,
  onEvent,
  overlay,
  onVideo,
}: {
  version: MediaVersion;
  scale?: number;
  replayKey?: number;
  onEvent?: (event: FrameEvent) => void;
  overlay?: ReactNode;
  onVideo?: (el: HTMLVideoElement | null) => void;
}) {
  const { w, h } = sizeOf(version);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const handler = useRef(onEvent);
  handler.current = onEvent;

  useEffect(() => {
    const listener = (e: MessageEvent) => {
      if (!iframeRef.current || e.source !== iframeRef.current.contentWindow) return;
      const data = e.data as { __zeigbanner?: number; type?: FrameEvent['type']; data?: FrameEvent['data'] };
      if (data?.__zeigbanner !== 1 || !data.type) return;
      handler.current?.({ type: data.type, data: data.data ?? {} });
    };
    window.addEventListener('message', listener);
    return () => window.removeEventListener('message', listener);
  }, []);

  return (
    <div className="frame-wrap" style={{ width: w * scale, height: h * scale }}>
      <div
        className="frame-scaler"
        style={{ width: w, height: h, transform: scale === 1 ? undefined : `scale(${scale})` }}
      >
        <MediaElement
          version={version}
          replayKey={replayKey}
          interactive
          iframeRef={iframeRef}
          videoRef={onVideo}
          controls
        />
      </div>
      {overlay}
    </div>
  );
}

/** Verkleinerte Live-Vorschau für Übersichten; wird erst geladen, wenn sie sichtbar ist. */
export function BannerThumb({
  version,
  maxWidth,
  maxHeight,
  upscale = false,
}: {
  version: MediaVersion;
  maxWidth: number;
  maxHeight: number;
  upscale?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), {
      rootMargin: '300px',
    });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  const { w, h } = sizeOf(version);
  const scale = Math.min(maxWidth / w, maxHeight / h, upscale ? 2 : 1);
  return (
    <div ref={ref} className="thumb-box" style={{ width: w * scale, height: h * scale }}>
      {visible && (
        <div className="thumb-inner" style={{ width: w, height: h, transform: `scale(${scale})` }}>
          <MediaElement version={version} replayKey={0} interactive={false} loop />
        </div>
      )}
      <div className="thumb-shield" />
    </div>
  );
}

/**
 * Größe des verfügbaren Platzes messen, um Banner passend zu skalieren. Callback-Ref,
 * damit auch Elemente erfasst werden, die erst nach dem Laden der Daten erscheinen.
 */
export function useElementSize<T extends HTMLElement>() {
  const [element, setElement] = useState<T | null>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  useEffect(() => {
    if (!element) return;
    const ro = new ResizeObserver(([entry]) => {
      const box = entry.contentRect;
      setSize({ width: Math.floor(box.width), height: Math.floor(box.height) });
    });
    ro.observe(element);
    return () => ro.disconnect();
  }, [element]);
  return [setElement, size] as const;
}
